import type { Trade } from "../types";
import { price, money2, num, utcShort, sideLabel, exitReason } from "../format";

export default function TradesTable({ trades, label }: { trades: Trade[]; label: string }) {
  if (!trades || trades.length === 0)
    return <p className="muted">No trades yet for {label}.</p>;

  return (
    <div>
      <div className="table-wrap" role="region" aria-label={`Trades for ${label}`} tabIndex={0}>
        <table className="trades">
          <thead>
            <tr>
              <th scope="col">Entry time (UTC)</th>
              <th scope="col">Side</th>
              <th scope="col">Entry $</th>
              <th scope="col">Exit time (UTC)</th>
              <th scope="col">Exit $</th>
              <th scope="col">Size</th>
              <th scope="col">P&amp;L</th>
              <th scope="col">R</th>
              <th scope="col">Bars</th>
              <th scope="col">Exit reason</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t, i) => {
              const er = exitReason(t.exit_reason);
              const long = sideLabel(t.side) === "Long";
              return (
                <tr key={i} className={t.pnl >= 0 ? "win" : "loss"}>
                  <td className="mono">{utcShort(t.entry_time)}</td>
                  <td><span className={`side-tag ${long ? "long" : "short"}`}>{long ? "▲ Long" : "▼ Short"}</span></td>
                  <td className="mono">{price(t.entry)}</td>
                  <td className="mono">{utcShort(t.exit_time)}</td>
                  <td className="mono">{price(t.exit)}</td>
                  <td className="mono">{num(t.size_oz, 2)}</td>
                  <td className={`mono ${t.pnl >= 0 ? "win" : "loss"}`}>{money2(t.pnl)}</td>
                  <td className="mono">{num(t.r_multiple)}</td>
                  <td className="mono">{t.bars_held}</td>
                  <td><span className={er.cls} title={er.explain}>{er.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <details className="legend">
        <summary>What do the exit reasons mean?</summary>
        <ul>
          <li><span className="exit tp">Take-profit</span> — price reached the profit target: full planned win (≈ +RR×risk).</li>
          <li><span className="exit sl">Stop-loss</span> — price hit the stop: full planned loss (−1R).</li>
          <li><span className="exit time">Time exit</span> — the position reached its max-hold limit (here 8 bars = 32h on the 4h timeframe) and was closed at the market price. It did <em>not</em> reach the target, so it banks whatever it had — a small win or a small loss. This is normal and expected for a mean-reversion/horizon strategy.</li>
        </ul>
      </details>
    </div>
  );
}
